import React, { useEffect, useMemo, useState } from 'react'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    DialogActions,
    FormControlLabel,
    IconButton,
    Switch,
    TableCell,
    TableRow,
    useTheme,
    Checkbox,
    Tooltip,
    MenuItem,
    TextField,
} from '@material-ui/core'
import NumberFormat from 'react-number-format'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { format } from 'date-fns'
import { useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router'
import {
    CellData,
    Container,
    HeadCell,
    RowData,
    SimpleTable,
} from '../../components'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import EmailIcon from '@material-ui/icons/Email'
import GetAppIcon from '@material-ui/icons/GetApp'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import { axiosInstance, isAccountManager, toMMDDYYYY } from '../../helpers'
import { useAppDispatch, useCompany, useUser } from '../../hooks'
import { Invoice, ListVendor, ModelMap } from '../../models'
import {
    getCompanyDetail,
    getCompanyInvoiceList,
    RootState,
    updateInvoice,
} from '../../store'
import { toast } from 'react-toastify'
import { useStyles } from '../../styles'
import { typographyStyle } from '../../theme/typographyStyle'
import { VendorInvoiceRequestActions } from './VendorInvoiceRequestActions'

type TenantVisibilityFilter = 'all' | 'visible' | 'hidden'
interface InvoiceRow extends RowData {
    id: CellData<number>
    date: CellData<Date>
    total_cost: CellData<number>
    invoice: CellData<Invoice>
}

const sanitizeFilenameSegment = (s: string) => {
    const cleaned = s
        .split('')
        .map((ch) => {
            const code = ch.charCodeAt(0)
            if (code <= 31) return '_'
            if (/[<>:"/\\|?*]/.test(ch)) return '_'
            return ch
        })
        .join('')
        .trim()
    return cleaned || 'export'
}

const getFilenameFromContentDisposition = (
    contentDisposition: string | undefined,
): string | null => {
    if (!contentDisposition) {
        return null
    }
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
    return match ? match[1] : null
}

export const InvoiceList = () => {
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const history = useHistory()

    const [invoiceRegenerationId, setInvoiceRegenerationId] = useState(-1)
    const [financialConfirm, setFinancialConfirm] = useState(false)
    const [resendingInvoiceId, setResendingInvoiceId] = useState<number | null>(
        null,
    )

    const [pageLoading, setPageLoading] = useState(true)
    const [csvExporting, setCsvExporting] = useState(false)
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<
        number | null
    >(null)
    const [exportStartDate, setExportStartDate] = useState<Date | null>(null)
    const [exportEndDate, setExportEndDate] = useState<Date | null>(null)
    const [
        tenantVisFilter,
        setTenantVisFilter,
    ] = useState<TenantVisibilityFilter>('all')
    const [togglingVisibilityId, setTogglingVisibilityId] = useState<
        number | null
    >(null)

    const companyDetail = useSelector(
        (state: RootState) => state.company.companyDetail,
    )

    const invoiceList = useSelector(
        (state: RootState) => state.invoice.companyInvoiceList,
    )

    const filteredInvoiceList = useMemo(() => {
        if (!invoiceList) {
            return []
        }
        return invoiceList.filter((invoice) => {
            const created = new Date(invoice.created_date)
            if (exportStartDate) {
                const start = new Date(exportStartDate)
                start.setHours(0, 0, 0, 0)
                if (created < start) {
                    return false
                }
            }
            if (exportEndDate) {
                const end = new Date(exportEndDate)
                end.setHours(23, 59, 59, 999)
                if (created > end) {
                    return false
                }
            }
            if (tenantVisFilter === 'visible' && !invoice.tenant_visible) {
                return false
            }
            if (tenantVisFilter === 'hidden' && invoice.tenant_visible) {
                return false
            }
            return true
        })
    }, [invoiceList, exportStartDate, exportEndDate, tenantVisFilter])

    const { vendorList, getVendorList } = useCompany()

    const { id } = useParams<{ id: string }>()
    const companyId = parseInt(id, 10)

    useEffect(() => {
        const companyDetailPromise = dispatch(
            getCompanyDetail({ company: companyId }),
        )

        let key: 'apartment' | 'vendor' = 'apartment'
        if (workspaceUser) {
            if (workspaceUser.active_workspace.company_type === 'APARTMENT') {
                key = 'vendor'
            }
        }

        const invoicePromise = dispatch(
            getCompanyInvoiceList({ params: { [key]: companyId } }),
        )

        const vendorPromise = getVendorList({ params: { my_team: true } })

        Promise.all([companyDetailPromise, invoicePromise, vendorPromise])
            .finally(() => setPageLoading(false))
            .catch((e) => toast.error(e.response.data.message))
    }, [])

    // Table setup
    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const cellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 400,
    }

    const headCells: HeadCell<InvoiceRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'id',
            label: 'Invoice #',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'date',
            label: 'Date',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'total_cost',
            label: 'Total Cost',
            style: headCellStyle,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'invoice',
            label: '',
            style: headCellStyle,
        },
    ]

    if (workspaceUser?.active_workspace.company_type === 'APARTMENT') {
        headCells.push({
            disablePadding: false,
            align: 'center',
            id: 'tenant_visible',
            label: 'Tenant Visible',
            style: headCellStyle,
        })
    }

    headCells.push({
        disablePadding: false,
        align: 'left',
        id: 'apartment_initiated_status',
        label: 'Vendor Response',
        style: headCellStyle,
    })

    const createRow = (row: Invoice): InvoiceRow => ({
        id: {
            value: row.id,
            sortValue: row.id,
        },
        date: {
            value: new Date(row.created_date),
            sortValue: new Date(row.created_date).valueOf(),
        },
        total_cost: {
            value: row.total_cost,
            sortValue: row.total_cost,
        },
        invoice: {
            value: row,
            sortValue: -1,
        },
    })
    // End Table Setup

    const loadInvoices = () => {
        let key: 'apartment' | 'vendor' = 'apartment'
        if (workspaceUser?.active_workspace.company_type === 'APARTMENT') {
            key = 'vendor'
        }
        dispatch(getCompanyInvoiceList({ params: { [key]: companyId } }))
    }

    const resendConfirmDenyEmail = (invoiceId: number) => {
        setResendingInvoiceId(invoiceId)
        axiosInstance
            .post(`invoice/${invoiceId}/resend_confirm_deny_email/`)
            .then(() => {
                toast.success('Confirm/deny email resent successfully')
            })
            .catch((e) => {
                toast.error(
                    e.response?.data?.message ?? 'Failed to resend email',
                )
            })
            .finally(() => setResendingInvoiceId(null))
    }

    const downloadInvoiceXlsx = async () => {
        setCsvExporting(true)
        try {
            let key: 'apartment' | 'vendor' = 'apartment'
            if (workspaceUser?.active_workspace.company_type === 'APARTMENT') {
                key = 'vendor'
            }

            const params: { [key: string]: unknown } = { [key]: companyId }
            if (exportStartDate) {
                params.greater_than_date = format(exportStartDate, 'yyyy-MM-dd')
            }
            if (exportEndDate) {
                params.less_than_date = format(exportEndDate, 'yyyy-MM-dd')
            }

            const response = await axiosInstance.get('invoice/export/', {
                params,
                responseType: 'blob',
            })

            const list = invoiceList ?? []
            const first = list[0]
            const vendorName = sanitizeFilenameSegment(
                first?.vendor?.name ?? 'Vendor',
            )
            const apartmentName = sanitizeFilenameSegment(
                first?.apartment?.name ?? 'Apartment',
            )
            const dateStr =
                exportStartDate || exportEndDate
                    ? `${
                          exportStartDate
                              ? format(exportStartDate, 'yyyy-MM-dd')
                              : 'start'
                      } to ${
                          exportEndDate
                              ? format(exportEndDate, 'yyyy-MM-dd')
                              : 'end'
                      }`
                    : new Date().toISOString().split('T')[0]
            const filename = `${vendorName} - ${apartmentName} - ${dateStr}.xlsx`

            const blob = new Blob([response.data], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => window.URL.revokeObjectURL(url), 100)
        } catch (e: unknown) {
            const msg =
                (e as { response?: { data?: { message?: string } } })?.response
                    ?.data?.message ?? 'Failed to download Excel file'
            toast.error(msg)
        } finally {
            setCsvExporting(false)
        }
    }

    const downloadSingleInvoiceXlsx = async (invoice: Invoice) => {
        setDownloadingInvoiceId(invoice.id)
        try {
            const response = await axiosInstance.get('invoice/export/', {
                params: { id: invoice.id },
                responseType: 'blob',
            })

            const headerFilename = getFilenameFromContentDisposition(
                response.headers['content-disposition'] as string | undefined,
            )
            const vendorName = sanitizeFilenameSegment(
                invoice.vendor?.name ?? 'Vendor',
            )
            const apartmentName = sanitizeFilenameSegment(
                invoice.apartment?.name ?? 'Apartment',
            )
            const filename =
                headerFilename ??
                `${vendorName} - ${apartmentName} - invoice_#${invoice.id}.xlsx`

            const blob = new Blob([response.data], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => window.URL.revokeObjectURL(url), 100)
        } catch (e: unknown) {
            const status = (e as { response?: { status?: number } })?.response
                ?.status
            if (status === 404) {
                toast.error('Invoice was not found or is not accessible')
            } else {
                toast.error('Failed to download Excel file')
            }
        } finally {
            setDownloadingInvoiceId(null)
        }
    }

    const toggleTenantVisibility = (invoice: Invoice, visible: boolean) => {
        setTogglingVisibilityId(invoice.id)
        axiosInstance
            .post(`invoice/${invoice.id}/toggle-tenant-visible/`, {
                tenant_visible: visible,
            })
            .then((res) => {
                dispatch(updateInvoice(res.data))
                toast.success(
                    visible
                        ? 'Invoice is now visible to tenants'
                        : 'Invoice hidden from tenants',
                )
            })
            .catch((e) => {
                toast.error(
                    e.response?.data?.message ??
                        'Failed to update tenant visibility',
                )
            })
            .finally(() => setTogglingVisibilityId(null))
    }

    const handleTenantVisToggle = (invoice: Invoice) => {
        toggleTenantVisibility(invoice, !invoice.tenant_visible)
    }

    const regenerateInvoice = async (
        recalculateFinance: boolean,
        invoiceId: number,
    ) => {
        axiosInstance
            .post(`invoice/${invoiceId}/regenerate_invoice/`, {
                recalculate_finances: recalculateFinance,
            })
            .then((res) => {
                toast.success(`Invoice ${invoiceId} Successfully Regenerated!`)
                dispatch(updateInvoice(res.data))
            })
            .catch(() => {
                toast.error('There was an error regenerating the invoice')
            })
    }

    const vendorMap: ModelMap<ListVendor> = {}
    vendorList.map((vendor) => (vendorMap[vendor.id] = vendor))

    if (pageLoading) {
        return (
            <Container alignItems="center" justifyContent="center" flex={1}>
                <CircularProgress
                    size={60}
                    style={{ marginTop: theme.spacing(10) }}
                />
            </Container>
        )
    }

    return (
        <Container
            direction="column"
            style={{
                flex: 1,
                height: 'calc(100vh - 104px)',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
            }}
        >
            {/* Header */}
            <Container
                style={{
                    marginBottom: theme.spacing(1),
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    minWidth: 0,
                    gap: theme.spacing(1),
                }}
            >
                <ArrowBackIosIcon
                    onClick={() => {
                        history.goBack()
                    }}
                    className={`${classes.hoverGrey300} ${classes.circle}`}
                    style={{
                        margin: theme.spacing(0, 0, 0, 1),
                        paddingLeft: theme.spacing(1),
                    }}
                    fontSize="large"
                />

                <span
                    style={{
                        ...typographyStyle(theme.typography.h4),
                        fontWeight: 700,
                    }}
                >
                    {companyDetail?.name}
                </span>

                <span
                    style={{
                        ...typographyStyle(theme.typography.subtitle1),
                        fontWeight: 300,
                        marginLeft: theme.spacing(1),
                    }}
                >
                    {filteredInvoiceList.length} Invoices
                </span>

                <Container
                    style={{
                        marginLeft: 'auto',
                        alignItems: 'center',
                        gap: theme.spacing(1),
                        flexWrap: 'wrap',
                        minWidth: 0,
                    }}
                >
                    {workspaceUser?.active_workspace.company_type ===
                        'APARTMENT' && (
                        <TextField
                            select
                            label="Tenant Visibility"
                            value={tenantVisFilter}
                            onChange={(e) =>
                                setTenantVisFilter(
                                    e.target.value as TenantVisibilityFilter,
                                )
                            }
                            variant="outlined"
                            size="small"
                            style={{ width: 180, flexShrink: 0 }}
                        >
                            <MenuItem value="all">All Invoices</MenuItem>
                            <MenuItem value="visible">Tenant Visible</MenuItem>
                            <MenuItem value="hidden">Hidden Only</MenuItem>
                        </TextField>
                    )}
                    <KeyboardDatePicker
                        value={exportStartDate}
                        onChange={(date) => setExportStartDate(date)}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="Start Date"
                        size="small"
                        maxDate={exportEndDate || undefined}
                        invalidDateMessage=""
                        clearable
                        autoOk
                        style={{ width: 200, flexShrink: 0 }}
                    />
                    <KeyboardDatePicker
                        value={exportEndDate}
                        onChange={(date) => setExportEndDate(date)}
                        format="MM/dd/yyyy"
                        inputVariant="outlined"
                        label="End Date"
                        size="small"
                        minDate={exportStartDate || undefined}
                        invalidDateMessage=""
                        clearable
                        autoOk
                        style={{ width: 200, flexShrink: 0 }}
                    />
                    <Button
                        variant="outlined"
                        color="primary"
                        disabled={csvExporting}
                        onClick={downloadInvoiceXlsx}
                        startIcon={
                            csvExporting ? (
                                <CircularProgress color="inherit" size={20} />
                            ) : (
                                <GetAppIcon />
                            )
                        }
                        style={{
                            textTransform: 'none',
                        }}
                    >
                        {csvExporting ? 'Preparing...' : 'Download Excel'}
                    </Button>
                </Container>
            </Container>

            <SimpleTable<InvoiceRow>
                rows={filteredInvoiceList.map(createRow)}
                render={(row) => (
                    <TableRow
                        key={`INVOICE__${row.id.value}`}
                        style={{ cursor: 'pointer' }}
                    >
                        <TableCell>
                            <span>INV #{row.id.value}</span>
                        </TableCell>
                        <TableCell style={cellStyle}>
                            <span>{toMMDDYYYY(row.date.value)}</span>
                        </TableCell>
                        <TableCell style={cellStyle}>
                            <NumberFormat
                                displayType="text"
                                value={row.total_cost.value}
                                prefix="$"
                                thousandSeparator
                                fixedDecimalScale
                                decimalScale={2}
                            />
                        </TableCell>
                        <TableCell align="center" style={cellStyle}>
                            <IconButton
                                disabled={
                                    row.invoice.value
                                        .apartment_initiated_status ===
                                    'PENDING'
                                }
                                onClick={() => {
                                    if (row.invoice.value.invoice) {
                                        window.open(row.invoice.value.invoice)
                                    } else {
                                        toast.error(
                                            `Invoice # ${row.invoice.value.id} was not found.  Please contact the support team.`,
                                        )
                                    }
                                }}
                            >
                                <PictureAsPdfIcon />
                            </IconButton>
                            <Tooltip title="Download Excel">
                                <span>
                                    <IconButton
                                        disabled={
                                            downloadingInvoiceId ===
                                                row.invoice.value.id ||
                                            row.invoice.value
                                                .apartment_initiated_status ===
                                                'PENDING'
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            downloadSingleInvoiceXlsx(
                                                row.invoice.value,
                                            )
                                        }}
                                    >
                                        {downloadingInvoiceId ===
                                        row.invoice.value.id ? (
                                            <CircularProgress
                                                size={20}
                                                color="inherit"
                                            />
                                        ) : (
                                            <GetAppIcon />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                            {isAccountManager(workspaceUser) && (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    disabled={
                                        row.invoice.value
                                            .apartment_initiated_status ===
                                        'PENDING'
                                    }
                                    onClick={() => {
                                        setInvoiceRegenerationId(
                                            row.invoice.value.id,
                                        )
                                    }}
                                >
                                    Regenerate
                                </Button>
                            )}
                        </TableCell>
                        {workspaceUser?.active_workspace.company_type ===
                            'APARTMENT' && (
                            <TableCell align="center" style={cellStyle}>
                                <Tooltip
                                    title={
                                        row.invoice.value.tenant_visible
                                            ? 'Visible to tenants — click to hide'
                                            : 'Hidden from tenants — click to show'
                                    }
                                >
                                    <span>
                                        <IconButton
                                            size="small"
                                            disabled={
                                                togglingVisibilityId ===
                                                row.invoice.value.id
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleTenantVisToggle(
                                                    row.invoice.value,
                                                )
                                            }}
                                        >
                                            {togglingVisibilityId ===
                                            row.invoice.value.id ? (
                                                <CircularProgress
                                                    size={20}
                                                    color="inherit"
                                                />
                                            ) : row.invoice.value
                                                  .tenant_visible ? (
                                                <VisibilityIcon
                                                    style={{
                                                        color:
                                                            theme.palette
                                                                .primary.main,
                                                    }}
                                                />
                                            ) : (
                                                <VisibilityOffIcon
                                                    style={{
                                                        color:
                                                            theme.palette
                                                                .grey[400],
                                                    }}
                                                />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </TableCell>
                        )}
                        <TableCell style={cellStyle}>
                            {row.invoice.value.apartment_initiated_status ? (
                                <Container
                                    style={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <VendorInvoiceRequestActions
                                        invoice={row.invoice.value}
                                        showActions={
                                            workspaceUser?.active_workspace
                                                .company_type === 'VENDOR'
                                        }
                                        onUpdated={loadInvoices}
                                    />
                                    {row.invoice.value
                                        .apartment_initiated_status ===
                                        'PENDING' &&
                                        workspaceUser?.active_workspace
                                            .company_type === 'APARTMENT' &&
                                        row.invoice.value.total_cost > 0 && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="primary"
                                                disabled={
                                                    resendingInvoiceId ===
                                                    row.invoice.value.id
                                                }
                                                onClick={() =>
                                                    resendConfirmDenyEmail(
                                                        row.invoice.value.id,
                                                    )
                                                }
                                                startIcon={
                                                    resendingInvoiceId ===
                                                    row.invoice.value.id ? (
                                                        <CircularProgress
                                                            size={14}
                                                            color="inherit"
                                                        />
                                                    ) : (
                                                        <EmailIcon fontSize="small" />
                                                    )
                                                }
                                                style={{
                                                    marginLeft: theme.spacing(
                                                        1,
                                                    ),
                                                    textTransform: 'none',
                                                }}
                                            >
                                                {resendingInvoiceId ===
                                                row.invoice.value.id
                                                    ? 'Sending...'
                                                    : 'Resend'}
                                            </Button>
                                        )}
                                </Container>
                            ) : (
                                '—'
                            )}
                        </TableCell>
                    </TableRow>
                )}
                orderByDefault="date"
                headCells={headCells}
            />

            <Dialog
                open={invoiceRegenerationId !== -1}
                onClose={() => setInvoiceRegenerationId(-1)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Please use caution
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        A financial recalculation will delete all the invoice
                        items attached to the invoice, and recreate them using
                        the workorders current financial information. Any
                        changes to service contracts, workorder add / custom
                        price, or change order financials will be reflected on
                        the new pdf.
                        <br />
                        <br />A superficial regeneration will recreate the pdf
                        leaving the financial calculations intact. This is
                        useful if an address has changed, or if the invoice pdf
                        failed to generate initially.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInvoiceRegenerationId(-1)}>
                        Cancel
                    </Button>
                    <div style={{ flex: 1 }} />
                    <Checkbox
                        checked={financialConfirm}
                        onChange={() => setFinancialConfirm((c) => !c)}
                    />
                    <Button
                        onClick={() => {
                            setInvoiceRegenerationId(-1)
                            regenerateInvoice(true, invoiceRegenerationId)
                        }}
                        color="secondary"
                        disabled={!financialConfirm}
                    >
                        Financial Recalculation
                    </Button>
                    <Button
                        onClick={() => {
                            setInvoiceRegenerationId(-1)
                            regenerateInvoice(false, invoiceRegenerationId)
                        }}
                        color="primary"
                        autoFocus
                    >
                        Superficial
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
